import {
  CmsComponentData,
  CmsComponentGrid,
  ContentComponentLibraryProvider,
} from '@johnlewispartnership/wtr-content-component-library';
import type { Config } from '@johnlewispartnership/wtr-content-component-library/dist/component-library/config/types';
import { useAnalytics } from '@johnlewispartnership/wtr-website-analytics';
import loadable from '@loadable/component';
import { BESPOKE_CONTENT_FRAGMENT_PATHS } from 'server/paths';

import { ContentPageData } from 'services/content/types';
import { PreloadedData } from 'server/types';
import cmsComponentMap from './componentMap';
import AnalyticsSetup from './components/AnalyticsSetup';

// For now, we always import the global grid styles
import './styles/grid.scss';
import siteWideBannerStyles from './styles/sitewide-banner.module.scss';

type AppProps = {
  componentData: CmsComponentData[];
  componentLibraryConfig: Omit<Config, 'componentMap'>;
  pageData?: ContentPageData;
  contentPath: string;
  preloadedData?: PreloadedData;
};

const PageLayout = loadable(() => import('./pages/PageLayout'));

// NOTE: This is used on both the server & client.
const AppContent = ({
  componentData,
  componentLibraryConfig,
  pageData,
  contentPath,
  preloadedData,
}: AppProps) => {
  const config: Config = {
    componentMap: cmsComponentMap,
    ...componentLibraryConfig,
  };
  const { trackEvent } = useAnalytics();
  const isSitewideBanner = contentPath === BESPOKE_CONTENT_FRAGMENT_PATHS['/sitewide-banner'].path;
  const position = isSitewideBanner ? 'sitewide-banner' : 'root';

  const contentComponents = (
    <ContentComponentLibraryProvider
      config={{
        ...config,
        onAnalyticsEvent: trackEvent,
        headFunction: metaData => metaData,
      }}
    >
      <CmsComponentGrid childComponents={componentData} position={position} preloadImages />
    </ContentComponentLibraryProvider>
  );

  if (contentPath === BESPOKE_CONTENT_FRAGMENT_PATHS['/sitewide-banner'].path) {
    return (
      <div role="region" aria-label="promotional banner" className={siteWideBannerStyles.container}>
        {contentComponents}
      </div>
    );
  }

  if (!pageData) {
    return contentComponents;
  }

  return (
    <PageLayout pageData={pageData} preloadedData={preloadedData}>
      {contentComponents}
    </PageLayout>
  );
};

// NOTE: This is used on both the server & client.
const App = ({
  componentData,
  componentLibraryConfig,
  pageData,
  contentPath,
  preloadedData,
}: AppProps) => {
  return (
    <AnalyticsSetup>
      <AppContent
        componentData={componentData}
        componentLibraryConfig={componentLibraryConfig}
        pageData={pageData}
        contentPath={contentPath}
        preloadedData={preloadedData}
      />
    </AnalyticsSetup>
  );
};

export default App;
